// Imposter Service Layer
// Handles Firebase Cloud Functions integration for Imposter game

import { doc, runTransaction, getDoc, setDoc, collection, getDocs, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  ImposterGame,
  ImposterSettings,
  ImposterStateDocument,
  ImposterSnapshot,
  PlayerId,
  DEFAULT_IMPOSTER_SETTINGS,
} from './logic';

const stateDocRef = (partyId: string) => 
  doc(db, 'parties', partyId, 'imposter_game', 'state');

const uuid = () => 
  (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : Math.random().toString(36).slice(2));

const now = () => Date.now();

const buildDoc = (
  partyId: string,
  game: ImposterGame,
  settings: ImposterSettings,
  createdAt?: number
): ImposterStateDocument => ({
  ...game.serialize(),
  partyId,
  createdAt: createdAt ?? now(),
  updatedAt: now(),
  settings,
});

// Start a new Imposter game
export const startImposterGame = async (
  partyId: string,
  players: PlayerId[],
  options?: { settings?: Partial<ImposterSettings> }
): Promise<ImposterStateDocument> => {
  if (players.length < 3) {
    throw new Error('At least 3 players required for Imposter');
  }

  const settings = { ...DEFAULT_IMPOSTER_SETTINGS, ...(options?.settings || {}) };
  const ref = stateDocRef(partyId);

  return runTransaction(db, async (tx) => {
    const existing = await tx.get(ref);
    if (existing.exists()) {
      const existingDoc = existing.data() as ImposterStateDocument;
      if (existingDoc.phase !== 'finished') {
        throw new Error('Imposter game already active');
      }
    }

    const game = new ImposterGame(players, settings);
    const docData = buildDoc(partyId, game, settings);
    tx.set(ref, docData);
    return docData;
  });
};

// Fetch current game state
export const fetchImposterState = async (
  partyId: string
): Promise<ImposterStateDocument | null> => {
  const snap = await getDoc(stateDocRef(partyId));
  return snap.exists() ? (snap.data() as ImposterStateDocument) : null;
};

// Apply a mutation to the game state
const applyGameMutation = async <T>(
  partyId: string,
  mutate: (game: ImposterGame) => T
): Promise<{ doc: ImposterStateDocument; result: T }> => {
  const ref = stateDocRef(partyId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      throw new Error('No active Imposter game');
    }

    const docData = snap.data() as ImposterStateDocument;
    const game = ImposterGame.fromSnapshot(docData);
    const result = mutate(game);

    const updatedDoc: ImposterStateDocument = {
      ...game.serialize(),
      partyId,
      createdAt: docData.createdAt,
      updatedAt: now(),
      settings: docData.settings,
    };

    tx.set(ref, updatedDoc);
    return { doc: updatedDoc, result };
  });
};

// Start the clue-giving phase
export const startCluePhase = async (
  partyId: string
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.startCluePhase();
  });
  return doc;
};

// Submit a clue from a player
export const submitClue = async (
  partyId: string,
  playerId: PlayerId,
  clue: string
): Promise<ImposterStateDocument> => {
  const { doc: updatedDoc } = await applyGameMutation(partyId, (game) => {
    const previousRound = game.snapshot.currentRound;
    const previousPlayerIndex = game.snapshot.currentPlayerIndex;
    
    game.submitClue(playerId, clue);
    
    // Check if we moved to a new round (currentPlayerIndex wrapped back to 0 and round incremented)
    const movedToNewRound = 
      game.snapshot.currentPlayerIndex === 0 && 
      game.snapshot.currentRound > previousRound &&
      game.snapshot.phase === 'clues'; // Still in clues phase (not voting)
    
    return { movedToNewRound, completedRound: previousRound };
  });
  
  const result = updatedDoc as any;
  
  // If we detected a new round starting, check vote preferences BEFORE resetting flags
  if (result.movedToNewRound) {
    // Get all player preferences first
    const playersCol = collection(db, 'parties', partyId, 'imposter_players');
    const snapshot = await getDocs(playersCol);
    const playerDocs = snapshot.docs;
    const playingPlayers = playerDocs.filter(doc => doc.data().status === 'playing');
    
    // Count who wants to vote (before resetting)
    const wantsToVoteCount = playingPlayers.filter(doc => doc.data().wantsToVote === true).length;
    const majority = Math.ceil(playingPlayers.length / 2);
    const shouldStartVoting = wantsToVoteCount >= majority && playingPlayers.length > 0;
    
    // Now reset player flags for the new round
    const updates = snapshot.docs.map(doc => 
      updateDoc(doc.ref, {
        hasSubmittedClue: false,
        hasVoted: false,
        wantsToVote: deleteField(), // Remove field so it's undefined again
        lastUpdatedAt: serverTimestamp(),
      })
    );
    await Promise.all(updates);
    
    // If majority wanted to vote, transition to voting phase
    if (shouldStartVoting) {
      await applyGameMutation(partyId, (game) => {
        game.forceVoting();
      });
    }
  }
  
  return updatedDoc;
};

// Submit a vote for who the imposter is
export const submitVote = async (
  partyId: string,
  voterId: PlayerId,
  suspectId: PlayerId
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.submitVote(voterId, suspectId);
  });
  return doc;
};

// Submit imposter's guess for the secret word
export const submitImposterGuess = async (
  partyId: string,
  guess: string
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.submitImposterGuess(guess);
  });
  return doc;
};

// Reveal results and calculate scores
export const revealAndScore = async (
  partyId: string
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.revealAndScore();
  });
  return doc;
};

// Force start voting phase (host override)
export const forceVoting = async (
  partyId: string
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.forceVoting();
  });
  return doc;
};

// Force advance to next clue round (host override)
export const forceNextRound = async (
  partyId: string
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.forceNextRound();
  });
  
  // Reset player flags for the new round
  const playersCol = collection(db, 'parties', partyId, 'imposter_players');
  const snapshot = await getDocs(playersCol);
  const updates = snapshot.docs.map(doc => 
    updateDoc(doc.ref, {
      hasSubmittedClue: false,
      hasVoted: false,
      wantsToVote: false,
      lastUpdatedAt: serverTimestamp(),
    })
  );
  await Promise.all(updates);
  
  return doc;
};

// Start a new round
export const startNewRound = async (
  partyId: string
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.startNewRound();
  });
  return doc;
};

// Add a player to an ongoing game
export const addImposterPlayer = async (
  partyId: string,
  playerId: PlayerId
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.addPlayer(playerId);
  });
  return doc;
};

// Remove a player from the game
export const removeImposterPlayer = async (
  partyId: string,
  playerId: PlayerId
): Promise<ImposterStateDocument> => {
  const { doc } = await applyGameMutation(partyId, (game) => {
    game.removePlayer(playerId);
  });
  return doc;
};

// End the game
export const endImposterGame = async (partyId: string): Promise<void> => {
  await runTransaction(db, async (tx) => {
    const ref = stateDocRef(partyId);
    const snap = await tx.get(ref);
    if (!snap.exists()) return;

    const docData = snap.data() as ImposterStateDocument;
    const game = ImposterGame.fromSnapshot(docData);

    const updated: ImposterStateDocument = {
      ...game.serialize(),
      phase: 'finished',
      partyId,
      createdAt: docData.createdAt,
      updatedAt: now(),
      settings: docData.settings,
    };

    tx.set(ref, updated);
  });
};
